import { useQuery } from '@tanstack/react-query';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { hrApi } from '../../services/hrApi.js';
import { formatCurrency } from '../../utils/formatters.js';
import { downloadPayslipPdf, previewPayslipPdf } from '../../utils/pdf.js';

/**
 * Per-employee payslips of one payroll run, with a Swiss payslip PDF per
 * row. Pass any run row (`{id, periodYear, periodMonth}`).
 */
export function PayslipsModal({ run, onClose }) {
  const { t } = useTranslation();
  const open = Boolean(run);

  const detail = useQuery({
    queryKey: ['hr', 'payroll', run?.id],
    queryFn: () => hrApi.getPayrollRun(run.id).then((r) => r.items),
    enabled: open,
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
    enabled: open,
  });

  const payslips = detail.data?.payslips || [];
  const period = run ? `${String(run.periodMonth).padStart(2, '0')}/${run.periodYear}` : '';

  // The PDF itemises the deduction lines (AHV/ALV/NBU/BVG); those components
  // live on the payslip detail endpoint, so fetch them right before building.
  async function withComponents(p) {
    try {
      const full = await hrApi.getPayslip(p.id).then((r) => r.items);
      return { ...p, components: full?.components || [] };
    } catch {
      return p; // fall back to the lump-sum rendering
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t('hr.payslips.title')} · ${period}`}
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
    >
      {detail.isLoading && <Spinner />}
      {detail.error && <div className="alert danger">{detail.error.message}</div>}
      {!detail.isLoading && payslips.length === 0 && (
        <p className="muted">{t('hr.payslips.empty')}</p>
      )}
      {payslips.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('hr.payslips.employee')}</th>
                <th className="num">{t('hr.payroll.col.gross')}</th>
                <th className="num">{t('hr.payroll.col.deductions')}</th>
                <th className="num">{t('hr.payroll.col.net')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payslips.map((p) => (
                <tr key={p.id}>
                  <td>
                    {[p.firstName, p.lastName].filter(Boolean).join(' ')}
                    <span className="muted text-sm"> · {p.employeeNumber}</span>
                  </td>
                  <td className="num mono">{formatCurrency(p.grossAmount, p.currency)}</td>
                  <td className="num mono">{formatCurrency(p.deductionsTotal, p.currency)}</td>
                  <td className="num mono"><strong>{formatCurrency(p.netAmount, p.currency)}</strong></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => previewPayslipPdf({ payslip: await withComponents(p), run: detail.data, settings: settings.data, t })}
                      >
                        {t('pdf.preview')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => downloadPayslipPdf({ payslip: await withComponents(p), run: detail.data, settings: settings.data, t })}
                      >
                        {t('invoice.print.downloadPdf')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
